import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../env";
import { showCmsErrorAlert, showCmsSuccessAlert } from "../../../Common/cmsAlerts";

const API_BASE = REACT_APP_API_URL.replace(/\/$/, "");
const apiUrl = (path) => `${API_BASE}${path}`;

const sectionTypes = [
  {
    value: "feature",
    label: "Feature Story",
    description: "Large story section with two images and text.",
    websiteView: "Appears in the top area as a major highlight.",
  },
  {
    value: "article",
    label: "Article",
    description: "Heading, paragraphs, optional image, and optional link.",
    websiteView: "Shows as a readable article-style section.",
  },
  {
    value: "card-grid",
    label: "Card Grid",
    description: "A group of highlight cards with images.",
    websiteView: "Shows as multiple visual cards in a row.",
  },
  {
    value: "banner",
    label: "Banner",
    description: "Large visual banner with date and location.",
    websiteView: "Shows as a wide promotional strip.",
  },
  {
    value: "event",
    label: "Event",
    description: "Event block with image, text, tags, date, and location.",
    websiteView: "Shows as the slim event layout on the website.",
  },
  {
    value: "quote",
    label: "Quote",
    description: "Visitor quote or testimonial with an optional image.",
    websiteView: "Shows as a quote block.",
  },
];

const sectionTypeLookup = sectionTypes.reduce((lookup, item) => {
  lookup[item.value] = item;
  return lookup;
}, {});

const emptyCard = () => ({
  title: "",
  description: "",
  image: "",
});

const defaultForm = {
  sectionType: "article",
  title: "",
  subtitle: "",
  eventDate: "",
  location: "",
  intro: "",
  paragraphs: [""],
  cards: [emptyCard()],
  tags: [""],
  quote: "",
  quoteAuthor: "",
  primaryImage: "",
  secondaryImage: "",
  imageAlt: "",
  secondaryImageAlt: "",
  imageCaption: "",
  linkUrl: "",
  linkLabel: "",
  displayOrder: 0,
  featured: false,
  isActive: true,
};

const assetUrl = (path) => {
  if (!path) return "";
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (path.startsWith("/eventUploads/")) return `${API_BASE}${path}`;
  return path;
};

const normalizeTextList = (value, fallback = [""]) => {
  const list = Array.isArray(value) ? value.map((item) => String(item ?? "")) : [];
  return list.length ? list : [...fallback];
};

const normalizeCards = (value) => {
  const list = Array.isArray(value)
    ? value.map((card) => ({
      title: card?.title || "",
      description: card?.description || "",
      image: card?.image || "",
    }))
    : [];

  return list.length ? list : [emptyCard()];
};

const buildFormState = (editData) => ({
  sectionType: editData?.sectionType || defaultForm.sectionType,
  title: editData?.title || "",
  subtitle: editData?.subtitle || "",
  eventDate: editData?.eventDate || "",
  location: editData?.location || "",
  intro: editData?.intro || "",
  paragraphs: normalizeTextList(editData?.paragraphs, defaultForm.paragraphs),
  cards: normalizeCards(editData?.cards),
  tags: normalizeTextList(editData?.tags, defaultForm.tags),
  quote: editData?.quote || "",
  quoteAuthor: editData?.quoteAuthor || "",
  primaryImage: editData?.primaryImage || "",
  secondaryImage: editData?.secondaryImage || "",
  imageAlt: editData?.imageAlt || "",
  secondaryImageAlt: editData?.secondaryImageAlt || "",
  imageCaption: editData?.imageCaption || "",
  linkUrl: editData?.linkUrl || "",
  linkLabel: editData?.linkLabel || "",
  displayOrder: editData?.displayOrder ?? 0,
  featured: !!editData?.featured,
  isActive: editData?.isActive !== false,
});

const trimTextList = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);

const trimCards = (cards) =>
  (Array.isArray(cards) ? cards : [])
    .map((card) => ({
      title: String(card?.title || "").trim(),
      description: String(card?.description || "").trim(),
      image: String(card?.image || "").trim(),
    }))
    .filter((card) => card.title || card.description || card.image);

const sanitizeFormBySection = (form) => {
  const sectionType = form.sectionType;
  const usesPrimaryImage = ["feature", "article", "banner", "quote", "event"].includes(sectionType);
  const usesSecondaryImage = sectionType === "feature";
  const usesParagraphs = ["feature", "article", "card-grid", "event"].includes(sectionType);
  const usesCards = sectionType === "card-grid";
  const usesTags = ["feature", "event"].includes(sectionType);
  const usesDateLocation = ["banner", "event"].includes(sectionType);
  const usesQuote = sectionType === "quote";
  const usesArticleExtras = ["article", "event"].includes(sectionType);

  return {
    ...form,
    title: String(form.title || "").trim(),
    subtitle: String(form.subtitle || "").trim(),
    intro: String(form.intro || "").trim(),
    paragraphs: usesParagraphs ? trimTextList(form.paragraphs) : [],
    cards: usesCards ? trimCards(form.cards) : [],
    tags: usesTags ? trimTextList(form.tags) : [],
    quote: usesQuote ? String(form.quote || "").trim() : "",
    quoteAuthor: usesQuote ? String(form.quoteAuthor || "").trim() : "",
    primaryImage: usesPrimaryImage ? String(form.primaryImage || "").trim() : "",
    secondaryImage: usesSecondaryImage ? String(form.secondaryImage || "").trim() : "",
    imageAlt: usesPrimaryImage ? String(form.imageAlt || "").trim() : "",
    secondaryImageAlt: usesSecondaryImage ? String(form.secondaryImageAlt || "").trim() : "",
    imageCaption: usesArticleExtras ? String(form.imageCaption || "").trim() : "",
    linkUrl: usesArticleExtras ? String(form.linkUrl || "").trim() : "",
    linkLabel: usesArticleExtras ? String(form.linkLabel || "").trim() : "",
    eventDate: usesDateLocation ? String(form.eventDate || "").trim() : "",
    location: usesDateLocation ? String(form.location || "").trim() : "",
    featured: sectionType === "feature" ? true : !!form.featured,
    isActive: !!form.isActive,
  };
};

const badgeClass = (active) => (active ? "bg-success" : "bg-secondary");

const ImageField = ({
  label,
  file,
  onFileChange,
  pathValue,
  onPathChange,
  previewAlt,
  helperText,
  detailsLabel = "Use an existing image path instead",
}) => (
  <div>
    <label className="form-label">{label}</label>
    <input
      type="file"
      accept="image/*"
      className="form-control"
      onChange={(event) => onFileChange(event.target.files?.[0] || null)}
    />
    {file ? (
      <div className="form-text">Selected file: {file.name}</div>
    ) : helperText ? (
      <div className="form-text">{helperText}</div>
    ) : null}

    {!file && pathValue && (
      <div className="mt-3">
        <div className="small text-muted mb-2">Current website image</div>
        <img
          src={assetUrl(pathValue)}
          alt={previewAlt}
          className="img-fluid rounded border"
          style={{ maxHeight: 180, objectFit: "cover" }}
        />
      </div>
    )}

    <details className="mt-3">
      <summary style={{ cursor: "pointer" }} className="small text-primary">
        {detailsLabel}
      </summary>
      <input
        className="form-control mt-2"
        placeholder="Paste an existing website image path"
        value={pathValue}
        onChange={(event) => onPathChange(event.target.value)}
      />
    </details>
  </div>
);

const ParagraphEditor = ({ paragraphs, setParagraphs }) => {
  const updateParagraph = (index, value) => {
    setParagraphs((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const addParagraph = () => {
    setParagraphs((current) => [...current, ""]);
  };

  const removeParagraph = (index) => {
    setParagraphs((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="card border-0 bg-light">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1">Paragraphs</h6>
            <p className="text-muted small mb-0">
              Add the written content exactly as it should appear on the website.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={addParagraph}>
            Add Paragraph
          </button>
        </div>

        <div className="row g-3">
          {paragraphs.map((paragraph, index) => (
            <div className="col-12" key={`paragraph-${index}`}>
              <label className="form-label">Paragraph {index + 1}</label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Write the content for this paragraph"
                value={paragraph}
                onChange={(event) => updateParagraph(index, event.target.value)}
              />
              {paragraphs.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger px-0 mt-1"
                  onClick={() => removeParagraph(index)}
                >
                  Remove paragraph
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TagEditor = ({ tags, setTags }) => {
  const updateTag = (index, value) => {
    setTags((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const addTag = () => {
    setTags((current) => [...current, ""]);
  };

  const removeTag = (index) => {
    setTags((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="card border-0 bg-light">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1">Highlight Tags</h6>
            <p className="text-muted small mb-0">
              These appear as hashtag-style highlights under the story.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={addTag}>
            Add Tag
          </button>
        </div>

        <div className="row g-3">
          {tags.map((tag, index) => (
            <div className="col-md-6" key={`tag-${index}`}>
              <label className="form-label">Tag {index + 1}</label>
              <input
                className="form-control"
                placeholder="Example: Heritage"
                value={tag}
                onChange={(event) => updateTag(index, event.target.value)}
              />
              {tags.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger px-0 mt-1"
                  onClick={() => removeTag(index)}
                >
                  Remove tag
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CardEditor = ({ cards, setCards, cardImageFiles, setCardImageFiles }) => {
  const updateCard = (index, field, value) => {
    setCards((current) =>
      current.map((card, cardIndex) =>
        cardIndex === index
          ? {
            ...card,
            [field]: value,
          }
          : card
      )
    );
  };

  const updateCardImageFile = (index, file) => {
    setCardImageFiles((current) => current.map((item, itemIndex) => (itemIndex === index ? file : item)));
  };

  const addCard = () => {
    setCards((current) => [...current, emptyCard()]);
    setCardImageFiles((current) => [...current, null]);
  };

  const removeCard = (index) => {
    setCards((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setCardImageFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="card border-0 bg-light">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1">Highlight Cards</h6>
            <p className="text-muted small mb-0">
              Add one card for each highlight you want visitors to see.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={addCard}>
            Add Card
          </button>
        </div>

        <div className="row g-3">
          {cards.map((card, index) => (
            <div className="col-12" key={`card-${index}`}>
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="mb-1">Card {index + 1}</h6>
                      <p className="text-muted small mb-0">
                        Title and image are usually the most important parts.
                      </p>
                    </div>
                    {cards.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeCard(index)}
                      >
                        Remove Card
                      </button>
                    )}
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Card Title</label>
                      <input
                        className="form-control"
                        value={card.title}
                        onChange={(event) => updateCard(index, "title", event.target.value)}
                        placeholder="Example: Procession Highlights"
                      />
                    </div>
                    <div className="col-md-6">
                      <ImageField
                        label="Card Image"
                        file={cardImageFiles[index]}
                        onFileChange={(file) => updateCardImageFile(index, file)}
                        pathValue={card.image}
                        onPathChange={(value) => updateCard(index, "image", value)}
                        previewAlt={card.title || `Card ${index + 1}`}
                        helperText="Upload the image for this card."
                        detailsLabel="Use an existing image path for this card"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Card Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Optional short description for this highlight"
                        value={card.description}
                        onChange={(event) => updateCard(index, "description", event.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EventSummaryView = ({ event }) => {
  const sectionMeta = sectionTypeLookup[event?.sectionType] || sectionTypes[0];
  const paragraphs = Array.isArray(event?.paragraphs) ? event.paragraphs : [];
  const tags = Array.isArray(event?.tags) ? event.tags : [];
  const cards = Array.isArray(event?.cards) ? event.cards : [];
  const isTopSection = event?.featured || event?.sectionType === "feature";

  return (
    <div className="row g-4">
      <div className="col-lg-5">
        {event?.primaryImage && (
          <img
            src={assetUrl(event.primaryImage)}
            alt={event?.title || "Section image"}
            className="img-fluid rounded border mb-3"
            style={{ maxHeight: 260, objectFit: "cover", width: "100%" }}
          />
        )}
        <div className="card">
          <div className="card-body">
            <h6 className="mb-3">Section Details</h6>
            <div className="small text-muted mb-1">Layout</div>
            <div className="mb-3">{sectionMeta.label}</div>

            <div className="small text-muted mb-1">Position on page</div>
            <div className="mb-3">{event?.displayOrder ?? 0}</div>

            <div className="small text-muted mb-1">Website status</div>
            <div className="mb-3">
              <span className={`badge ${badgeClass(event?.isActive)}`}>
                {event?.isActive ? "Published" : "Hidden"}
              </span>
              {isTopSection && <span className="badge bg-info text-dark ms-2">Top Section</span>}
            </div>

            {event?.eventDate && (
              <>
                <div className="small text-muted mb-1">Event Date</div>
                <div className="mb-3">{event.eventDate}</div>
              </>
            )}

            {event?.location && (
              <>
                <div className="small text-muted mb-1">Location</div>
                <div>{event.location}</div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-7">
        <h4 className="mb-1">{event?.title || "Untitled Section"}</h4>
        {event?.subtitle && <p className="text-muted">{event.subtitle}</p>}

        {paragraphs.length > 0 && (
          <div className="mb-4">
            <h6>Paragraphs</h6>
            {paragraphs.map((paragraph, index) => (
              <p className="mb-2" key={`view-paragraph-${index}`}>
                {paragraph}
              </p>
            ))}
          </div>
        )}

        {cards.length > 0 && (
          <div className="mb-4">
            <h6>Cards</h6>
            <div className="row g-3">
              {cards.map((card, index) => (
                <div className="col-md-6" key={`view-card-${index}`}>
                  <div className="card h-100">
                    {card.image && (
                      <img
                        src={assetUrl(card.image)}
                        alt={card.title || `Card ${index + 1}`}
                        className="card-img-top"
                        style={{ height: 160, objectFit: "cover" }}
                      />
                    )}
                    <div className="card-body">
                      <div className="fw-semibold">{card.title || `Card ${index + 1}`}</div>
                      {card.description && <p className="text-muted small mb-0 mt-2">{card.description}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tags.length > 0 && (
          <div className="mb-4">
            <h6>Highlight Tags</h6>
            <div className="d-flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span className="badge bg-light text-dark border" key={tag}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {event?.quote && (
          <div className="mb-4">
            <h6>Quote</h6>
            <blockquote className="border-start ps-3 mb-0">
              {event.quote}
              {event.quoteAuthor && <div className="fw-semibold mt-2">- {event.quoteAuthor}</div>}
            </blockquote>
          </div>
        )}

        {event?.linkUrl && (
          <div className="mb-3">
            <h6>Link</h6>
            <a href={event.linkUrl} target="_blank" rel="noreferrer">
              {event.linkLabel || event.linkUrl}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const EventForm = ({ editData, onSuccess }) => {
  const [form, setForm] = useState(defaultForm);
  const [primaryImageFile, setPrimaryImageFile] = useState(null);
  const [secondaryImageFile, setSecondaryImageFile] = useState(null);
  const [cardImageFiles, setCardImageFiles] = useState([null]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const nextForm = buildFormState(editData);
    setForm(nextForm);
    setPrimaryImageFile(null);
    setSecondaryImageFile(null);
    setCardImageFiles(Array(nextForm.cards.length).fill(null));
  }, [editData]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const sectionMeta = sectionTypeLookup[form.sectionType] || sectionTypes[0];
  const showParagraphs = ["feature", "article", "card-grid", "event"].includes(form.sectionType);
  const showTags = ["feature", "event"].includes(form.sectionType);
  const showCards = form.sectionType === "card-grid";
  const showBannerFields = ["banner", "event"].includes(form.sectionType);
  const showQuoteFields = form.sectionType === "quote";
  const showPrimaryImage = ["feature", "article", "banner", "quote", "event"].includes(form.sectionType);
  const showSecondaryImage = form.sectionType === "feature";
  const showArticleExtras = ["article", "event"].includes(form.sectionType);
  const featureLocksTopPosition = form.sectionType === "feature";

  const titleLabel = form.sectionType === "quote" ? "Section Name" : "Section Title";
  const titleHint =
    form.sectionType === "quote"
      ? "This helps staff identify the quote block. Visitors mainly see the quote text."
      : "This appears as the main heading on the website.";

  const submit = async (event) => {
    event.preventDefault();

    const cleaned = sanitizeFormBySection(form);

    if (!cleaned.title) {
      alert("Please enter a section title before saving.");
      return;
    }

    if (cleaned.sectionType === "quote" && !cleaned.quote) {
      alert("Please add the quote text before saving.");
      return;
    }

    if (cleaned.sectionType === "card-grid" && cleaned.cards.length === 0) {
      alert("Please add at least one card before saving.");
      return;
    }

    setSaving(true);

    const formData = new FormData();
    formData.append("sectionType", cleaned.sectionType);
    formData.append("title", cleaned.title);
    formData.append("subtitle", cleaned.subtitle);
    formData.append("eventDate", cleaned.eventDate);
    formData.append("location", cleaned.location);
    formData.append("intro", cleaned.intro);
    formData.append("paragraphs", JSON.stringify(cleaned.paragraphs));
    formData.append("cards", JSON.stringify(cleaned.cards));
    formData.append("tags", JSON.stringify(cleaned.tags));
    formData.append("quote", cleaned.quote);
    formData.append("quoteAuthor", cleaned.quoteAuthor);
    formData.append("primaryImage", cleaned.primaryImage);
    formData.append("secondaryImage", cleaned.secondaryImage);
    formData.append("imageAlt", cleaned.imageAlt);
    formData.append("secondaryImageAlt", cleaned.secondaryImageAlt);
    formData.append("imageCaption", cleaned.imageCaption);
    formData.append("linkUrl", cleaned.linkUrl);
    formData.append("linkLabel", cleaned.linkLabel);
    formData.append("displayOrder", String(cleaned.displayOrder ?? 0));
    formData.append("featured", cleaned.featured ? "1" : "0");
    formData.append("isActive", cleaned.isActive ? "1" : "0");

    if (primaryImageFile) formData.append("primaryImage", primaryImageFile);
    if (secondaryImageFile) formData.append("secondaryImage", secondaryImageFile);

    cleaned.cards.forEach((_, index) => {
      if (cardImageFiles[index]) {
        formData.append(`cardImage_${index}`, cardImageFiles[index]);
      }
    });

    try {
      if (editData?.intEventID) {
        await axios.put(apiUrl(`/api/events/admin/${editData.intEventID}`), formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(apiUrl("/api/events/admin"), formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const refreshed = await onSuccess?.();
      if (refreshed !== false) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="alert alert-info">
        <div className="fw-semibold mb-1">{sectionMeta.label}</div>
        <div>{sectionMeta.description}</div>
        <div className="small mt-1">
          <strong>On website:</strong> {sectionMeta.websiteView}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Choose Layout</label>
          <select
            className="form-select"
            value={form.sectionType}
            onChange={(event) => setField("sectionType", event.target.value)}
          >
            {sectionTypes.map((type) => (
              <option value={type.value} key={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-5">
          <label className="form-label">{titleLabel} *</label>
          <input
            className="form-control"
            value={form.title}
            onChange={(event) => setField("title", event.target.value)}
            placeholder="Example: Sarhul Celebration 2026"
            required
          />
          <div className="form-text">{titleHint}</div>
        </div>

        <div className="col-md-3">
          <label className="form-label">Section Position</label>
          <input
            type="number"
            className="form-control"
            value={form.displayOrder}
            onChange={(event) => setField("displayOrder", event.target.value)}
          />
          <div className="form-text">Lower numbers appear earlier on the page.</div>
        </div>

        <div className="col-md-8">
          <label className="form-label">Supporting Line</label>
          <input
            className="form-control"
            value={form.subtitle}
            onChange={(event) => setField("subtitle", event.target.value)}
            placeholder="Optional smaller line under the title"
          />
        </div>

        <div className="col-md-4 d-flex align-items-end">
          <div className="w-100 border rounded p-3">
            <label className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setField("isActive", event.target.checked)}
              />
              <span className="form-check-label ms-2">Publish on Website</span>
            </label>

            {featureLocksTopPosition ? (
              <div className="small text-muted">
                Feature story sections always appear in the top area.
              </div>
            ) : (
              <label className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={form.featured}
                  onChange={(event) => setField("featured", event.target.checked)}
                />
                <span className="form-check-label ms-2">Show in Top Section</span>
              </label>
            )}
          </div>
        </div>

        {showBannerFields && (
          <>
            <div className="col-md-6">
              <label className="form-label">Date Text</label>
              <input
                className="form-control"
                value={form.eventDate}
                onChange={(event) => setField("eventDate", event.target.value)}
                placeholder="Example: 18 June 2026"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Location Text</label>
              <input
                className="form-control"
                value={form.location}
                onChange={(event) => setField("location", event.target.value)}
                placeholder="Example: Ranchi"
              />
            </div>
          </>
        )}

        {showQuoteFields && (
          <>
            <div className="col-12">
              <label className="form-label">Quote Text</label>
              <textarea
                className="form-control"
                rows="4"
                value={form.quote}
                onChange={(event) => setField("quote", event.target.value)}
                placeholder="Write the visitor quote or testimonial"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Quote Author</label>
              <input
                className="form-control"
                value={form.quoteAuthor}
                onChange={(event) => setField("quoteAuthor", event.target.value)}
                placeholder="Example: Visitor Name"
              />
            </div>
          </>
        )}

        {showPrimaryImage && (
          <div className={showSecondaryImage ? "col-md-6" : "col-12"}>
            <ImageField
              label={showBannerFields ? "Banner Image" : "Main Image"}
              file={primaryImageFile}
              onFileChange={setPrimaryImageFile}
              pathValue={form.primaryImage}
              onPathChange={(value) => setField("primaryImage", value)}
              previewAlt={form.title || "Main image"}
              helperText="Upload the image that should appear in this section."
            />
          </div>
        )}

        {showSecondaryImage && (
          <div className="col-md-6">
            <ImageField
              label="Second Image"
              file={secondaryImageFile}
              onFileChange={setSecondaryImageFile}
              pathValue={form.secondaryImage}
              onPathChange={(value) => setField("secondaryImage", value)}
              previewAlt={form.title || "Second image"}
              helperText="Optional supporting image for the feature story."
            />
          </div>
        )}

        {showArticleExtras && (
          <>
            <div className="col-md-6">
              <label className="form-label">Image Caption</label>
              <input
                className="form-control"
                value={form.imageCaption}
                onChange={(event) => setField("imageCaption", event.target.value)}
                placeholder="Optional caption below the image"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Link URL</label>
              <input
                className="form-control"
                value={form.linkUrl}
                onChange={(event) => setField("linkUrl", event.target.value)}
                placeholder="Optional website link"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Link Button Text</label>
              <input
                className="form-control"
                value={form.linkLabel}
                onChange={(event) => setField("linkLabel", event.target.value)}
                placeholder="Example: Read More"
              />
            </div>
          </>
        )}

        {showParagraphs && (
          <div className="col-12">
            <ParagraphEditor
              paragraphs={form.paragraphs}
              setParagraphs={(updater) =>
                setForm((current) => ({
                  ...current,
                  paragraphs:
                    typeof updater === "function" ? updater(current.paragraphs) : updater,
                }))
              }
            />
          </div>
        )}

        {showTags && (
          <div className="col-12">
            <TagEditor
              tags={form.tags}
              setTags={(updater) =>
                setForm((current) => ({
                  ...current,
                  tags: typeof updater === "function" ? updater(current.tags) : updater,
                }))
              }
            />
          </div>
        )}

        {showCards && (
          <div className="col-12">
            <CardEditor
              cards={form.cards}
              setCards={(updater) =>
                setForm((current) => ({
                  ...current,
                  cards: typeof updater === "function" ? updater(current.cards) : updater,
                }))
              }
              cardImageFiles={cardImageFiles}
              setCardImageFiles={setCardImageFiles}
            />
          </div>
        )}

        <div className="col-12">
          <details>
            <summary style={{ cursor: "pointer" }} className="fw-semibold">
              Advanced Options
            </summary>

            <div className="row g-3 mt-2">
              {showPrimaryImage && (
                <div className="col-md-6">
                  <label className="form-label">Main Image Description</label>
                  <input
                    className="form-control"
                    value={form.imageAlt}
                    onChange={(event) => setField("imageAlt", event.target.value)}
                    placeholder="Optional description for screen readers"
                  />
                </div>
              )}

              {showSecondaryImage && (
                <div className="col-md-6">
                  <label className="form-label">Second Image Description</label>
                  <input
                    className="form-control"
                    value={form.secondaryImageAlt}
                    onChange={(event) => setField("secondaryImageAlt", event.target.value)}
                    placeholder="Optional description for screen readers"
                  />
                </div>
              )}

              <div className="col-12">
                <label className="form-label">Internal Note</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={form.intro}
                  onChange={(event) => setField("intro", event.target.value)}
                  placeholder="Optional note for future reference"
                />
                <div className="form-text">This is stored in the database but is not shown on the website.</div>
              </div>
            </div>
          </details>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button className="btn btn-success" disabled={saving}>
          {saving ? "Saving..." : "Save Section"}
        </button>
      </div>
    </form>
  );
};

const PageSettingsForm = ({ page, onSuccess }) => {
  const [form, setForm] = useState({
    pageTitle: "",
    heroAlt: "",
    heroImage: "",
  });
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      pageTitle: page?.title || "",
      heroAlt: page?.heroAlt || "",
      heroImage: page?.heroImage || "",
    });
    setHeroImageFile(null);
  }, [page]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append("pageTitle", form.pageTitle);
    formData.append("heroAlt", form.heroAlt);
    formData.append("heroImage", form.heroImage);

    if (heroImageFile) formData.append("heroImage", heroImageFile);

    try {
      await axios.put(apiUrl("/api/events/admin/page-settings"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const refreshed = await onSuccess?.();
      if (refreshed !== false) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header fw-bold">Page Hero</div>
      <div className="card-body">
        <p className="text-muted mb-3">
          This controls the large banner at the top of the public Events page.
        </p>

        <form onSubmit={submit}>
          <div className="row g-4 align-items-start">

            {/* Left Column */}
            <div className="col-lg-6">

              <div className="mb-3">
                <label className="form-label">Hero Heading</label>

                <input
                  className="form-control"
                  value={form.pageTitle}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      pageTitle: event.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="form-label">Image Description</label>

                <input
                  className="form-control"
                  value={form.heroAlt}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      heroAlt: event.target.value,
                    }))
                  }
                  placeholder="Optional description for screen readers"
                />
              </div>

            </div>

            {/* Right Column */}
            <div className="col-lg-6">

              <ImageField
                label="Hero Image"
                file={heroImageFile}
                onFileChange={setHeroImageFile}
                pathValue={form.heroImage}
                onPathChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    heroImage: value,
                  }))
                }
                previewAlt={form.pageTitle || "Events hero"}
                helperText="Upload the image that should appear at the top of the page."
                detailsLabel="Use an existing image path for the hero"
              />

            </div>

          </div>

          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Hero"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EventContentManager = () => {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("add");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.displayOrder - b.displayOrder),
    [events]
  );

  const loadEvents = async (silent = false) => {
    setLoading(true);
    try {
      const res = await axios.get(apiUrl("/api/events/admin"));
      setEvents(res.data.data || []);
      setPage(res.data.page || {});
      return true;
    } catch (err) {
      if (!silent) showCmsErrorAlert(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const openModal = (type, row = null) => {
    setActionType(type);
    setSelectedEvent(row);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const closeAndRefresh = async () => {
    closeModal();
    return loadEvents(true);
  };

  const toggleStatus = async (row) => {
    try {
      await axios.patch(apiUrl(`/api/events/admin/${row.intEventID}/status`), {
        isActive: row.isActive ? 0 : 1,
      });
      if (await loadEvents(true)) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    }
  };

  const deleteEvent = async (row) => {
    if (!window.confirm("Delete this section from the Events page?")) return;

    try {
      await axios.delete(apiUrl(`/api/events/admin/${row.intEventID}`));
      if (await loadEvents(true)) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Events Page Manager</h4>
          <p className="text-muted mb-0">
            Update the top hero and add page sections for the public Events page.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal("add")}>
          Add Section
        </button>
      </div>

      <div className="alert alert-light border">
        <div className="fw-semibold mb-1">How this page works</div>
        <div className="small text-muted">
          First update the page hero if needed. Then add sections below it. Each section becomes one
          block on the public Events page.
        </div>
      </div>

      <PageSettingsForm page={page} onSuccess={loadEvents} />

      <div className="card">
        <div className="card-header fw-bold">Content Sections</div>
        <div className="card-body table-responsive">
          {loading ? (
            <div className="p-3">Loading...</div>
          ) : (
            <table className="table table-bordered align-middle">
              <thead>
                <tr>
                  <th width="90">Position</th>
                  <th>Section</th>
                  <th width="170">Layout</th>
                  <th width="160">Website Status</th>
                  <th width="320">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No sections have been added yet.
                    </td>
                  </tr>
                )}

                {sortedEvents.map((row) => {
                  const sectionMeta = sectionTypeLookup[row.sectionType] || sectionTypes[0];
                  const isTopSection = row.featured || row.sectionType === "feature";

                  return (
                    <tr key={row.intEventID}>
                      <td>{row.displayOrder}</td>
                      <td>
                        <div className="fw-semibold">{row.title}</div>
                        {row.subtitle && <div className="text-muted small mt-1">{row.subtitle}</div>}
                        {isTopSection && (
                          <span className="badge bg-info text-dark mt-2">Top Section</span>
                        )}
                      </td>
                      <td>{sectionMeta.label}</td>
                      <td>
                        <span className={`badge ${badgeClass(row.isActive)}`}>
                          {row.isActive ? "Published" : "Hidden"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openModal("view", row)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => openModal("edit", row)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleStatus(row)}
                          >
                            {row.isActive ? "Hide" : "Publish"}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteEvent(row)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {actionType === "add"
                      ? "Add Section"
                      : actionType === "edit"
                        ? "Edit Section"
                        : "Section Details"}
                  </h5>
                  <button className="btn-close" onClick={closeModal} />
                </div>
                <div className="modal-body">
                  {actionType === "view" ? (
                    <EventSummaryView event={selectedEvent} />
                  ) : (
                    <EventForm
                      editData={actionType === "edit" ? selectedEvent : null}
                      onSuccess={closeAndRefresh}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default EventContentManager;
